import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  RowSelectionOptions,
  themeQuartz,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { useCallback, useMemo } from "react";
import { Currency, WorkOrderStatus } from "@/gql/graphql";
import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import { ReportWorkOrderListItem } from "./GraphQL/reportWorkOrdersQuery";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { WorkOrderExpenseDetails } from "./WorkOrderExpenseDetails";
import { workOrderMessages } from "@/app/ProDrivers/WorkOrders/messages/workOrderMessages";

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
};

const sidebar = {
  toolPanels: ["columns", "filters"],
};

interface ReportWorkOrdersTableProps {
  ref: any;
  data: ReportWorkOrderListItem[];
  isLoading: boolean;
}

function isSummaryRow(data: unknown): boolean {
  return (data as { _isSummaryRow?: boolean })?._isSummaryRow === true;
}

function rowCurrency(data: ReportWorkOrderListItem | undefined): Currency {
  return (data?.currency as Currency) ?? Currency.Aud;
}

export function ReportWorkOrdersTable({
  ref,
  data,
  isLoading,
}: ReportWorkOrdersTableProps) {
  const formatMoney = useCallback(
    (
      value: number | null | undefined,
      data: ReportWorkOrderListItem | undefined,
    ) => {
      const currency = isSummaryRow(data) ? Currency.Aud : rowCurrency(data);
      return formatCurrency(value, currency, { emptyValue: "" });
    },
    [],
  );

  const formatAmountCell = useCallback(
    (params: ValueFormatterParams<ReportWorkOrderListItem, number>) =>
      formatMoney(params.value, params.data),
    [formatMoney],
  );

  const buildMoneyValueGetter = useCallback(
    (
      woField: keyof ReportWorkOrderListItem,
      audField: keyof ReportWorkOrderListItem,
    ) =>
      (params: ValueGetterParams<ReportWorkOrderListItem>): number | null => {
        if (!params.data) return null;
        const field = isSummaryRow(params.data) ? audField : woField;
        return (params.data[field] as number | null | undefined) ?? 0;
      },
    [],
  );

  const columns = useMemo(() => {
    return [
      {
        headerName: "",
        field: "expand" as any,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          suppressCount: true,
          suppressDoubleClickExpand: true,
          innerRenderer: () => null,
        },
        width: 50,
        pinned: "left",
        lockPosition: true,
        suppressMovable: true,
      },
      {
        field: "work_order_reference",
        headerName: "Work Order #",
        filter: "agTextColumnFilter",
        pinned: "left",
        cellRenderer: (
          params: ICellRendererParams<ReportWorkOrderListItem>,
        ) => {
          if (!params.data?.id || isSummaryRow(params.data)) {
            return params.value;
          }
          const id = params.data.id;
          return (
            <Link
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
              params={(prev) => ({ ...prev, id })}
              className="text-blue-600 hover:underline"
            >
              {params.value}
            </Link>
          );
        },
      },
      {
        field: "departure_city",
        headerName: "Route",
        filter: "agTextColumnFilter",
        valueGetter: (params) => {
          if (!params.data?.departure_city && !params.data?.delivery_city) {
            return null;
          }
          const from = params.data?.departure_city ?? "—";
          const to = params.data?.delivery_city ?? "—";
          return `${from} → ${to}`;
        },
      },
      {
        field: "supplier_name",
        headerName: "Supplier",
        filter: "agTextColumnFilter",
      },
      {
        field: "work_order_status",
        headerName: "Status",
        filter: "agSetColumnFilter",
        filterParams: {
          values: Object.values(WorkOrderStatus),
          valueFormatter: (params: { value: WorkOrderStatus }) =>
            workOrderMessages.status[params.value] ?? params.value,
        },
        enableRowGroup: true,
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value === undefined) {
            return null;
          }

          const status = params.value as WorkOrderStatus;
          let colorClass = "bg-gray-100 text-gray-800 border-gray-200";

          switch (status) {
            case WorkOrderStatus.InProgress:
              colorClass = "bg-green-100 text-green-800 border-green-200";
              break;
            case WorkOrderStatus.DriversAssigned:
              colorClass = "bg-emerald-100 text-emerald-800 border-emerald-200";
              break;
            case WorkOrderStatus.AssigningDrivers:
              colorClass = "bg-yellow-100 text-yellow-800 border-yellow-200";
              break;
            case WorkOrderStatus.QuoteRequested:
              colorClass = "bg-zinc-100 text-zinc-800 border-zinc-200";
              break;
            case WorkOrderStatus.QuoteIssued:
              colorClass = "bg-violet-100 text-violet-800 border-violet-200";
              break;
            case WorkOrderStatus.Completed:
              colorClass = "bg-sky-100 text-sky-800 border-sky-200";
              break;
            case WorkOrderStatus.Invoiced:
              colorClass = "bg-blue-100 text-blue-800 border-blue-200";
              break;
          }

          const label =
            workOrderMessages.status[status] ?? status.replace(/_/g, " ");
          const badgeClass = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`;

          return <div className={badgeClass}>{label}</div>;
        },
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 110,
        filter: "agSetColumnFilter",
        valueGetter: (params) => {
          if (isSummaryRow(params.data)) return Currency.Aud;
          return params.data?.currency ?? null;
        },
      },
      {
        field: "invoice_reference",
        headerName: "Invoice #",
        filter: "agTextColumnFilter",
      },
      {
        colId: "invoice_amount",
        headerName: "Invoice Amount",
        valueGetter: buildMoneyValueGetter(
          "invoice_amount_exc_tax_wo_currency",
          "invoice_amount_exc_tax_aud",
        ),
        valueFormatter: formatAmountCell,
      },
      {
        colId: "claimed_expenses",
        headerName: "Expenses",
        valueGetter: buildMoneyValueGetter(
          "total_claimed_expenses",
          "total_claimed_expenses_aud",
        ),
        valueFormatter: formatAmountCell,
      },
      {
        colId: "profit",
        headerName: "Profit",
        valueGetter: buildMoneyValueGetter("profit", "profit_aud"),
        valueFormatter: formatAmountCell,
        cellRenderer: (
          params: ICellRendererParams<ReportWorkOrderListItem, number>,
        ) => {
          const amount = params.value ?? 0;
          const formattedAmount = formatMoney(params.value, params.data);

          if (amount < 0) {
            return (
              <span className="font-medium text-red-600">
                {formattedAmount}
              </span>
            );
          }

          if (amount > 0) {
            return (
              <span className="font-medium text-green-600">
                {formattedAmount}
              </span>
            );
          }

          return <span>{formattedAmount}</span>;
        },
      },
      {
        colId: "expected_payout",
        headerName: "Expected Payout",
        valueGetter: buildMoneyValueGetter(
          "total_expected_payout",
          "total_expected_payout_aud",
        ),
        valueFormatter: formatAmountCell,
      },
      {
        colId: "payout_total",
        headerName: "Payout Total",
        valueGetter: buildMoneyValueGetter(
          "total_payouts",
          "total_payouts_aud",
        ),
        valueFormatter: formatAmountCell,
        cellRenderer: (
          params: ICellRendererParams<ReportWorkOrderListItem, number>,
        ) => {
          const formattedAmount = formatMoney(params.value, params.data);
          const isSummary = isSummaryRow(params.data);
          const expected = isSummary
            ? (params.data?.total_expected_payout_aud ?? 0)
            : (params.data?.total_expected_payout ?? 0);
          const actual = params.value ?? 0;

          if (actual !== expected) {
            return (
              <span className="font-medium text-amber-700">
                {formattedAmount}
              </span>
            );
          }

          return <span>{formattedAmount}</span>;
        },
      },
      {
        colId: "margin",
        headerName: "Margin %",
        valueGetter: (params) => {
          if (isSummaryRow(params.data)) {
            return (params.data as any)?._avgMargin ?? null;
          }
          const invoiceAmount =
            params.data?.invoice_amount_exc_tax_wo_currency || 0;
          if (invoiceAmount === 0) return null;
          const profit = params.data?.profit || 0;
          return (profit / invoiceAmount) * 100;
        },
        valueFormatter: (params) => {
          if (params.value == null) return "";
          return `${params.value.toFixed(1)}%`;
        },
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value == null) return null;
          const margin = params.value as number;
          const isSummary = isSummaryRow(params.data);

          let bgColor = "";
          let textColor = "text-gray-900";

          if (margin < 0) {
            bgColor = "bg-red-100";
            textColor = "text-red-800";
          } else if (margin < 10) {
            bgColor = "bg-green-50";
            textColor = "text-green-700";
          } else if (margin < 20) {
            bgColor = "bg-green-100";
            textColor = "text-green-700";
          } else if (margin < 30) {
            bgColor = "bg-green-200";
            textColor = "text-green-800";
          } else if (margin < 40) {
            bgColor = "bg-green-300";
            textColor = "text-green-800";
          } else if (margin < 50) {
            bgColor = "bg-green-400";
            textColor = "text-green-900";
          } else {
            bgColor = "bg-green-500";
            textColor = "text-white";
          }

          return (
            <span
              className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${bgColor} ${textColor}`}
            >
              {isSummary ? "Avg: " : ""}
              {margin.toFixed(1)}%
            </span>
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        valueGetter: (params) => {
          if (!params.data?.created_at) return null;
          return dayjs.utc(params.data.created_at).toDate();
        },
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs(params.value).format("DD/MM/YYYY");
        },
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: any) => {
            if (!cellValue) return -1;
            const normalizedFilterDate = dayjs(
              filterLocalDateAtMidnight,
            ).startOf("day");
            const normalizedCellDate = dayjs(cellValue).startOf("day");

            if (normalizedFilterDate.isBefore(normalizedCellDate, "day"))
              return 1;
            if (normalizedFilterDate.isAfter(normalizedCellDate, "day"))
              return -1;
            return 0;
          },
        },
      },
    ] as ColDef<ReportWorkOrderListItem>[];
  }, [buildMoneyValueGetter, formatAmountCell, formatMoney]);

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return {
      mode: "multiRow",
    };
  }, []);

  const getPinnedBottomRowData = useCallback(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const totalWorkOrders = data.length;

    const sum = (key: keyof ReportWorkOrderListItem) =>
      data.reduce(
        (acc, wo) => acc + ((wo[key] as number | null | undefined) ?? 0),
        0,
      );

    const totalInvoiceAmountAud = sum("invoice_amount_exc_tax_aud");
    const totalClaimedExpensesAud = sum("total_claimed_expenses_aud");
    const totalExpectedPayoutAud = sum("total_expected_payout_aud");
    const totalPayoutsAud = sum("total_payouts_aud");
    const totalProfitAud = sum("profit_aud");

    const workOrdersWithInvoice = data.filter(
      (wo) =>
        wo.invoice_amount_exc_tax_aud && wo.invoice_amount_exc_tax_aud > 0,
    );
    const avgMargin =
      workOrdersWithInvoice.length > 0
        ? workOrdersWithInvoice.reduce((acc, wo) => {
            const margin =
              ((wo.profit_aud || 0) / wo.invoice_amount_exc_tax_aud!) * 100;
            return acc + margin;
          }, 0) / workOrdersWithInvoice.length
        : null;

    return [
      {
        work_order_reference: `Total Work Orders: ${totalWorkOrders} (AUD)`,
        currency: Currency.Aud,
        invoice_amount_exc_tax_aud: totalInvoiceAmountAud,
        total_claimed_expenses_aud: totalClaimedExpensesAud,
        total_expected_payout_aud: totalExpectedPayoutAud,
        total_payouts_aud: totalPayoutsAud,
        profit_aud: totalProfitAud,
        _isSummaryRow: true,
        _avgMargin: avgMargin,
      },
    ];
  }, [data]);

  const getRowStyle = useCallback((params: any) => {
    if (params.data?._isSummaryRow) {
      return {
        backgroundColor: "#f3f4f6",
      };
    }
    return undefined;
  }, []);

  const detailCellRenderer = useCallback((params: any) => {
    const workOrder = params.data as ReportWorkOrderListItem;
    return (
      <WorkOrderExpenseDetails
        workOrderId={workOrder.id}
        workOrderCurrency={(workOrder.currency as Currency) ?? Currency.Aud}
      />
    );
  }, []);

  return (
    <AgGridReact<ReportWorkOrderListItem>
      className="grow"
      ref={ref}
      loading={isLoading}
      rowData={data}
      rowSelection={rowSelection}
      theme={imoovaTheme}
      columnDefs={columns}
      defaultColDef={defaultColDef}
      enableCharts={true}
      cellSelection={true}
      sideBar={sidebar}
      pinnedBottomRowData={getPinnedBottomRowData()}
      getRowStyle={getRowStyle}
      masterDetail={true}
      detailCellRenderer={detailCellRenderer}
      detailRowAutoHeight={true}
    />
  );
}
